import { brandColor } from '../../../_helpers/brand-color';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, skip } from 'rxjs';
import { ChartData, ChartOptions } from 'chart.js';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { StatsFilterService } from '../statistics-filter.service';
import {
  StatsDataService,
  RevenueByTreatment,
} from '../statistics-data.service';

// Components
import { StatChartComponent } from '../_components/stat-chart/stat-chart.component';

// PrimeNG
import { TableModule } from 'primeng/table';

@Component({
  standalone: true,
  selector: 'app-stats-treatments',
  templateUrl: './treatments.component.html',
  styleUrls: ['./treatments.component.scss'],
  imports: [CommonModule, TranslateModule, StatChartComponent, TableModule],
})
export class StatsTreatmentsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];

  public loading: boolean = true;
  public treatments: RevenueByTreatment[] = [];

  // Charts
  public performanceChartData: ChartData | null = null;
  public performanceChartOptions: ChartOptions = {};
  public bubbleChartData: ChartData | null = null;

  constructor(
    public session: SessionService,
    private filterService: StatsFilterService,
    private dataService: StatsDataService,
    private translate: TranslateService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadData();

    this.subscriptions.push(
      this.filterService.filters$
        .pipe(skip(1))
        .subscribe(() => this.loadData()),
    );

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        if (this.treatments.length) {
          this.buildPerformanceChart();
          this.buildBubbleChart();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadData(): void {
    const customerId = this.session.getCustomer()?.id;
    const siteId = this.session.getSite()?.id;

    if (!customerId || !siteId) return;

    this.loading = true;
    this.dataService.getRevenueByTreatment(customerId, siteId).subscribe({
      next: (data) => {
        this.treatments = data.sort((a, b) => b.revenue - a.revenue);
        this.buildPerformanceChart();
        this.buildBubbleChart();
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  private buildPerformanceChart(): void {
    const top10 = this.treatments.slice(0, 10);
    this.performanceChartData = {
      labels: top10.map((t) => t.treatmentName),
      datasets: [
        {
          label: this.translate.instant('STATISTICS.TREATMENTS.CHARTS.REVENUE'),
          data: top10.map((t) => t.revenue),
          backgroundColor: brandColor(),
          borderRadius: 4,
        },
      ],
    };

    this.performanceChartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) =>
              new Intl.NumberFormat(this.getLocale(), {
                style: 'currency',
                currency: 'EUR',
              }).format(ctx.parsed.x),
          },
        },
      },
      scales: {
        x: {
          grid: { color: '#f1f5f9' },
          ticks: {
            color: '#64748b',
            callback: (v) =>
              typeof v === 'number' ? `${(v / 1000).toFixed(0)}k€` : v,
          },
        },
        y: {
          grid: { display: false },
          ticks: { color: '#334155', font: { size: 12 } },
        },
      },
    };
  }

  private buildBubbleChart(): void {
    // Profitability matrix: X = count, Y = avgPrice, Size = revenue
    const maxRevenue = Math.max(...this.treatments.map((t) => t.revenue));
    this.bubbleChartData = {
      datasets: this.treatments.map((t, i) => ({
        label: t.treatmentName,
        data: [
          {
            x: t.count,
            y: t.averagePrice,
            r: Math.max(5, (t.revenue / maxRevenue) * 30),
          },
        ],
        backgroundColor: this.getColor(i, 0.6),
        borderColor: this.getColor(i, 1),
        borderWidth: 1,
      })),
    };
  }

  private getColor(index: number, alpha: number): string {
    const colors = [
      brandColor(500, alpha),
      `rgba(139, 92, 246, ${alpha})`,
      `rgba(236, 72, 153, ${alpha})`,
      `rgba(245, 158, 11, ${alpha})`,
      `rgba(16, 185, 129, ${alpha})`,
      `rgba(6, 182, 212, ${alpha})`,
    ];
    return colors[index % colors.length];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat(this.getLocale(), {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  getTrendClass(trend: number | undefined): string {
    if (!trend || Math.abs(trend) < 0.01) return 'neutral';
    return trend > 0 ? 'positive' : 'negative';
  }

  getMostPerformedTreatment(): string {
    if (!this.treatments || this.treatments.length === 0) return '-';
    const sorted = [...this.treatments].sort((a, b) => b.count - a.count);
    return sorted[0]?.treatmentName || '-';
  }

  private getLocale(): string {
    const lang = (this.translate.currentLang || 'es').toLowerCase();
    if (lang.startsWith('en')) return 'en-US';
    if (lang.startsWith('ca')) return 'ca-ES';
    return 'es-ES';
  }
}
